import { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Link } from 'react-router'

// evertel
import { UnreadCountsState, UserController, UserSettingsController } from '@evertel/blue-user'
import { useService } from '@evertel/di'
import { Badge, Card, CardBody, Col, ContentPlaceholder, FloatingMenu, Icon, MenuItem, Row, Spinner } from '@evertel/web/ui'
import { ProfilePic } from '@evertel/web/user'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { NormalizedMedia } from '@evertel/media'
import { DisplayRoomMessage } from '@evertel/stores'
import { SessionService } from '@evertel/session/SessionService'
import ReplyTileBody from './ReplyTileBody'
import { MessageHeader } from '../MessageHeader'


interface ReplyTileProps {
    message: DisplayRoomMessage
    media?: NormalizedMedia[]
    onSelect?: (message: DisplayRoomMessage) => void
    onUpdateNotifyState?: (updateState: boolean | null, message: DisplayRoomMessage) => void
    loadingNotification?: boolean
}

const ReplyTile: React.FC<ReplyTileProps> = observer(({
    message,
    media,
    onSelect = () => { /** */ },
    onUpdateNotifyState = () => { /** */ },
    loadingNotification
}) => {

    const sessionService = useService(SessionService, [])
    const userController = useService(UserController, [message.ownerId])
    const userSettingsController = useService(UserSettingsController, [message.ownerId])
    const daController = useService(DepartmentsAccessController, [message.ownerId])
    const unreadState = useService(UnreadCountsState, [])

    useEffect(() => {
        setIsLoading(true)

        userController.init(message.ownerId)
        daController.init(message.ownerId)
        setIsLoading(false)
    }, [message.ownerId])

    const handleUpdateNotify = (notifyState, e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault()
        e.stopPropagation()
        onUpdateNotifyState(notifyState, message)
    }

    const onFloatingMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
        // prevent the propagation of the click to the tile itself
        e.preventDefault()
        e.stopPropagation()
    }

    const [isLoading, setIsLoading] = useState(false)
    const isGuest = daController.selectedDepartmentRole === 'guest' || false

    const replyNotificationSetting = message.meta?.repliesSubscription?.notify

    const roomNotificationSettings = userSettingsController.roomNotificationSettings[message?.roomId]
    const roomNotificationsEnabled = (roomNotificationSettings) ? roomNotificationSettings.roomMessage?.enabled : true

    const replyNotificationEnabled = replyNotificationSetting === null
        ? roomNotificationsEnabled
        : replyNotificationSetting

    const replyUnreadObj = unreadState?.getReplyUnread(message?.roomId, message?.id)

    if (isLoading) {
        return (
            <Card
                className="reply-tile ms-4 mb-4"
            >
                <CardBody>
                    <Row className="mt-2">
                        <ContentPlaceholder
                            width={40}
                            height={40}
                            color="gray200"
                            className="me-2"
                        />
                        <ContentPlaceholder
                            width="40%"
                            height={20}
                            color="gray200"
                            className="mt-1"
                        />
                    </Row>
                    <div className="ms-5">
                        <ContentPlaceholder
                            width="70%"
                            color="gray200"
                            className="mt-1"
                        />
                        <ContentPlaceholder
                            width="80%"
                            color="gray200"
                            className="mt-1"
                        />
                        <ContentPlaceholder
                            width="90%"
                            color="gray200"
                            className="mt-1"
                        />
                    </div>
                </CardBody>
            </Card>
        )

    }

    return (
        <Link className="reply-tile-link" to={`/room/${message.roomId}/replies/${message.id}`}
            onClick={() => onSelect(message)}
            style={{ textDecoration: 'none', color: 'inherit' }}
        >
            <Card className="reply-tile mb-2">
                <CardBody>
                    <Row>
                        <Col valign="top" className="left-col pt-1 me-2">
                            <ProfilePic
                                userId={userController?.user?.id}
                                isGuest={isGuest}
                            />
                        </Col>
                        <Col valign="top" className="center-col" xs={10}>
                            <MessageHeader
                                message={message}
                                user={userController?.user}
                                isGuest={isGuest}
                                variant="reply"

                            />
                            <ReplyTileBody
                                message={message}
                                media={media}
                            />
                        </Col>
                    </Row>
                    <div className="reply-footer mt-4">
                        <div className="text-primary fw-bold">
                            <span>{message.meta.repliesUsersCount} {message.meta.repliesUsersCount !== 1 ? 'people' : 'person'}</span>
                            <span>  | {message.meta.repliesCount} {message.meta.repliesCount !== 1 ? 'replies' : 'reply'}</span>
                            <span className="ms-2">
                                {replyUnreadObj?.unreadCount &&
                                    <Badge
                                        color={`${replyUnreadObj.urgentCount ? 'danger' : 'warning'}`}
                                    >
                                        {<strong>{replyUnreadObj?.unreadCount}</strong>}
                                    </Badge>
                                }
                            </span>
                        </div>
                        <FloatingMenu
                            label="Notifications"
                            onClick={onFloatingMenuOpen}
                            className="reply-notification-menu"
                            renderLabel={() => (
                                <div className="position-relative">
                                    {loadingNotification ? (
                                        <Spinner width={15} />
                                    ):(
                                        <>
                                            {replyNotificationEnabled ?
                                                <Icon name={'bell'} type={'solid'} size={15} color={'primary'} fixedWidth={true} />
                                                :
                                                <Icon name={'bell-slash'} size={15} fixedWidth={true} />
                                            }
                                            {replyNotificationSetting !== null && (
                                                <span className="position-absolute" style={{ fontSize: 18, top: -9, right: -2, opacity: 0.6 }}>*</span>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        >
                            <MenuItem
                                label="Same as Room"
                                onClick={(e) => handleUpdateNotify(null, e)}
                            >
                                <div className="d-flex flex-row align-items-center">
                                    <input type="radio" readOnly checked={replyNotificationSetting === null} className="notifications-radio-button" />
                                    <div className="d-flex flex-column ms-3">
                                        <strong>Auto</strong>
                                        <small>Reply notifications will match room settings</small>
                                    </div>
                                </div>
                            </MenuItem>
                            <MenuItem
                                label="Always on"
                                onClick={(e) => handleUpdateNotify(true, e)}
                            >
                                <div className="d-flex flex-row align-items-center">
                                    <input type="radio" readOnly checked={replyNotificationSetting === true} className="notifications-radio-button" />
                                    <div className="d-flex flex-column ms-3">
                                        <strong>Receive notifications for this reply</strong>
                                        <small>Reply notifications will override room settings</small>
                                    </div>
                                </div>
                            </MenuItem>
                            <MenuItem
                                label="Always off"
                                onClick={(e) => handleUpdateNotify(false, e)}
                            >
                                <div className="d-flex flex-row align-items-center">
                                    <input type="radio" readOnly checked={replyNotificationSetting === false} style={{ accentColor: 'red' }} />
                                    <div className="d-flex flex-column ms-3">
                                        <strong className="text-danger">Mute</strong>
                                        <small>Reply notifications will override room settings</small>
                                    </div>
                                </div>
                            </MenuItem>
                        </FloatingMenu>
                    </div>
                </CardBody>
            </Card>
        </Link>
    )
})

export { ReplyTile }
